import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Mail, ShieldCheck, Send, Info, ArrowLeft, CalendarClock, Fish } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { useToast } from "../hooks/use-toast";
import PageHero from "../components/PageHero";
import { images, membershipRequest } from "../mock/mock";

export default function Mitgliedschaft() {
  const { toast } = useToast();
  const [form, setForm] = useState({ vorname: "", nachname: "", email: "" });
  const [accepted, setAccepted] = useState(false);
  const [opened, setOpened] = useState(false);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  const canSubmit =
    form.vorname.trim() && form.nachname.trim() && emailValid && accepted;

  const buildMailto = () => {
    const subject = encodeURIComponent(membershipRequest.subject);
    const body = encodeURIComponent(
      `Hallo,\n\n` +
        `folgender Kandidat hat zwecks einer Mitgliedschaft angefragt und die Bedingungen des Vereins ausdrücklich akzeptiert:\n\n` +
        `Nachname: ${form.nachname}\n` +
        `Vorname: ${form.vorname}\n` +
        `E-Mail: ${form.email}\n\n` +
        `Bitte auf die Warteliste eintragen.\n\n` +
        `Viele Grüße\n${form.vorname} ${form.nachname}`
    );
    return `mailto:${membershipRequest.recipient}?subject=${subject}&body=${body}`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    window.location.href = buildMailto();
    setOpened(true);
    toast({
      title: "E-Mail-Programm wird geöffnet",
      description: "Bitte die vorbereitete E-Mail nur noch absenden.",
    });
  };

  return (
    <div>
      <PageHero
        eyebrow="Mitglied werden"
        title="Auf die Warteliste"
        subtitle="Interesse an einer Mitgliedschaft? Bedingungen lesen, bestätigen und unverbindlich anfragen."
        image={images.anglerMisty}
      />

      <section className="py-20 bg-background">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <p className="text-lg text-muted-foreground font-300 leading-relaxed">
            {membershipRequest.intro}
          </p>

          {/* Aktuelle Situation */}
          <div className="mt-10 bg-primary text-primary-foreground rounded-2xl p-7">
            <div className="flex items-center gap-3 mb-5">
              <CalendarClock className="h-7 w-7 text-accent shrink-0" />
              <h2 className="font-serif text-2xl font-600">
                {membershipRequest.situation.title}
              </h2>
            </div>
            <ul className="space-y-3">
              {membershipRequest.situation.points.map((p, i) => (
                <li key={i} className="flex items-start gap-3 text-primary-foreground/90">
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-accent shrink-0" />
                  <span className="leading-relaxed">{p}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Bedingungen / Abschnitte */}
          <div className="mt-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center">
                <ShieldCheck className="h-6 w-6 text-primary" />
              </div>
              <h2 className="font-serif text-2xl font-600 text-foreground">
                Grundlegende Informationen zur Mitgliedschaft
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              {membershipRequest.sections.map((sec) => (
                <div
                  key={sec.title}
                  className="bg-card border border-border rounded-2xl p-6"
                >
                  <h3 className="font-serif text-xl font-600 text-primary mb-3">
                    {sec.title}
                  </h3>
                  <ul className="space-y-2.5">
                    {sec.items.map((it, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                        <CheckCircle2 className="h-4 w-4 text-accent mt-0.5 shrink-0" />
                        <span className="leading-relaxed">{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Was wir bieten */}
          <div className="mt-8 bg-secondary/50 border border-border rounded-2xl p-7">
            <div className="flex items-center gap-3 mb-5">
              <div className="h-11 w-11 rounded-xl bg-accent/15 flex items-center justify-center">
                <Fish className="h-6 w-6 text-accent" />
              </div>
              <h2 className="font-serif text-2xl font-600 text-foreground">
                Was wir bieten
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {membershipRequest.benefits.map((b, i) => (
                <div key={i} className="flex items-start gap-2.5 text-muted-foreground">
                  <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                  <span className="leading-relaxed">{b}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Formular */}
          {opened ? (
            <div className="mt-10 bg-card border border-border rounded-2xl p-8 text-center">
              <CheckCircle2 className="h-14 w-14 text-primary mx-auto mb-4" />
              <h3 className="font-serif text-2xl font-600 text-foreground">
                Fast geschafft, {form.vorname || "danke"}!
              </h3>
              <p className="text-muted-foreground mt-2 max-w-lg mx-auto leading-relaxed">
                Dein E-Mail-Programm sollte sich mit einer vorbereiteten Nachricht
                geöffnet haben. Bitte sende diese einfach ab – wir melden uns und
                setzen dich auf die Warteliste. Sollte sich nichts geöffnet haben,
                schreibe uns direkt an{" "}
                <a
                  href={`mailto:${membershipRequest.recipient}?subject=${encodeURIComponent(membershipRequest.subject)}`}
                  className="text-primary font-600 underline underline-offset-2 break-all"
                >
                  {membershipRequest.recipient}
                </a>
                .
              </p>
              <Button
                variant="outline"
                className="mt-6 border-primary/30 text-primary hover:bg-secondary gap-2"
                onClick={() => {
                  setOpened(false);
                  setForm({ vorname: "", nachname: "", email: "" });
                  setAccepted(false);
                }}
              >
                <ArrowLeft className="h-4 w-4" /> Neue Anfrage
              </Button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="mt-10 bg-card border border-border rounded-2xl p-8 shadow-sm space-y-5"
            >
              <h3 className="font-serif text-2xl font-600 text-foreground">
                Deine Anfrage
              </h3>
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="vorname">Vorname</Label>
                  <Input
                    id="vorname"
                    name="vorname"
                    value={form.vorname}
                    onChange={handleChange}
                    placeholder="Max"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="nachname">Nachname</Label>
                  <Input
                    id="nachname"
                    name="nachname"
                    value={form.nachname}
                    onChange={handleChange}
                    placeholder="Mustermann"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">E-Mail-Adresse</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="max@beispiel.de"
                />
              </div>

              {/* Pflicht-Zustimmung */}
              <div className="flex items-start gap-3 bg-secondary/50 border border-border rounded-xl p-4">
                <Checkbox
                  id="accept"
                  checked={accepted}
                  onCheckedChange={(v) => setAccepted(v === true)}
                  className="mt-0.5"
                />
                <Label
                  htmlFor="accept"
                  className="text-sm text-foreground font-400 leading-relaxed cursor-pointer"
                >
                  {membershipRequest.acceptLabel}
                </Label>
              </div>

              {!accepted && (
                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Info className="h-4 w-4 shrink-0" />
                  Bitte bestätige die Bedingungen, um deine Anfrage zu senden.
                </p>
              )}

              <Button
                type="submit"
                disabled={!canSubmit}
                className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-600 gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="h-4 w-4" /> Anfrage per E-Mail senden
              </Button>
              <p className="text-xs text-muted-foreground text-center leading-relaxed">
                Beim Klick öffnet sich dein E-Mail-Programm mit einer vorbereiteten
                Nachricht an {membershipRequest.recipient}. Du sendest sie selbst ab –
                so bleibt deine Anfrage unverbindlich.
              </p>
            </form>
          )}

          <div className="mt-8 text-center">
            <Link to="/kontakt" className="inline-flex items-center gap-2 text-sm font-500 text-primary hover:text-primary/80">
              <Mail className="h-4 w-4" /> Allgemeine Fragen? Zur Kontaktseite
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
